import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";

const C = OPS.wm.admin;

const NUMBERS = [
    ["backdate_days", "wm_admin_backdate_days"],
    ["edit_days", "wm_admin_edit_days"],
    ["day_cutoff_hour", "wm_admin_cutoff_hour"],
    ["warn_quantity", "wm_admin_warn_quantity"],
    ["max_quantity", "wm_admin_max_quantity"],
];

/** The spec leaves one policy to management — «سبب الهدر: اختياري أو إجباري حسب
 *  سياسة الإدارة» — plus the back-date and edit windows the module enforces. */
export function PolicyCard() {
    const { t, lang } = useI18n();
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/waste-meals/settings");
            setForm(r.data);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        }
    }, [lang, t]);

    useEffect(() => { load(); }, [load]);

    if (!form) return <InlineLoader />;

    const save = async () => {
        setBusy(true);
        try {
            const body = { reason_required: !!form.reason_required };
            for (const [k] of NUMBERS) body[k] = Number(form[k]);
            const r = await api.put("/waste-meals/settings", body);
            setForm(r.data);
            toast.success(t("wm_saved"));
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={C.policyCard}
             className="max-w-xl space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-4">
            <label className="flex items-center justify-between rounded-lg border border-[color:var(--border-soft)] px-3 py-2">
                <span>
                    <span className="block text-xs font-bold text-[color:var(--ink)]">{t("wm_admin_reason_required")}</span>
                    <span className="block text-[10px] text-[color:var(--ink-faint)]">{t("wm_admin_reason_required_hint")}</span>
                </span>
                <Switch data-testid={C.policyReasonRequired} checked={!!form.reason_required}
                        onCheckedChange={(v) => setForm({ ...form, reason_required: v })} />
            </label>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {NUMBERS.map(([k, label]) => (
                    <div key={k}>
                        <Label className="text-[11px] font-bold">{t(label)}</Label>
                        <Input data-testid={C.policyField(k)} type="number" dir="ltr" value={form[k] ?? ""}
                               onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                               className="mt-1 h-9" />
                    </div>
                ))}
            </div>

            <Button data-testid={C.policySave} onClick={save} disabled={busy} className="h-9 text-xs font-semibold">
                {t("save")}
            </Button>
        </div>
    );
}
